import { useCallback } from 'react';
import { SnackbarType } from 'common/components/Notifications';
import TelemetryProvider, { Properties } from 'common/utilities/TelemetryProvider';
import { useLocalizedStrings } from 'i18n';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { hideNotification, showNotification } from 'store/layout/layoutSlice';

import { SeverityLevel } from '@microsoft/applicationinsights-web';

import { getToastNotifications } from '../layoutSelectors';
import { INotification, NotificationType } from '../layoutState';

/**
 * Helper hook for managing global notifications
 */
export const useNotifications = () => {
  const { t } = useLocalizedStrings();
  const toasts = useAppSelector((state) => getToastNotifications(state.layout));
  const dispatch = useAppDispatch();

  const showToast = useCallback(
    (toast: Omit<INotification, 'type' | 'id'> & { id?: string }) => {
      void dispatch(showNotification({ ...toast, id: toast.id || Date.now.toString(), type: NotificationType.Toast }));
    },
    [dispatch]
  );

  const dismissNotification = useCallback(
    (notificationId: string) => {
      void dispatch(hideNotification(notificationId));
    },
    [dispatch]
  );

  /**
   * Spawn a generic error toast
   *
   * @param error
   * @param eventName - The event name to use for the telemetry
   * @param properties - Additional data used to filter events and metrics in the portal. Defaults to empty.
   * @param measurements - Metrics associated with this event, displayed in Metrics Explorer on the portal. Defaults to empty.
   * @param severityLevel - Severity level
   * @param id - Unique guid identifying this error (not the toast)
   * @param dismissable - Whether the toast is dismissable
   */
  const toastGenericError = useCallback(
    (
      error: unknown,
      eventName?: string,
      properties?: Properties,
      measurements?: { [name: string]: number },
      severityLevel?: SeverityLevel,
      id?: string,
      dismissable?: boolean
    ) => {
      showToast({ message: t('SomethingWentWrong'), messageType: SnackbarType.error, dismissable: !!dismissable });
      TelemetryProvider.Instance.trackUnknownException(error, eventName, properties, measurements, severityLevel, id);
    },
    [showToast, t]
  );

  return { toasts, showToast, dismissNotification, toastGenericError };
};
